import React, { useEffect, useState, useMemo } from 'react'
import { ActionList, Button, ButtonGroup, Card, DataTable, FormLayout, Layout, Modal, Popover, Spinner, TextContainer, TextField } from '@shopify/polaris'
import { MoreHoriz as MoreIcon } from '@material-ui/icons';
import { Check as CheckIcon, Close as CloseIcon } from '@material-ui/icons';
import { Checkbox, FormControlLabel } from '@material-ui/core';

export default function CalendarSettings({ calendar, syncCalendarWindows, handleSave, updateShopConfig }) {
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [calendarPopup, setCalendarPopup] = useState(false);
  const [form, setForm] = useState(calendar);
  const [activeType, setActiveType] = useState('pickup');
  const [syncWindows, setSyncWindows] = useState(syncCalendarWindows !== false);

  // Normalize legacy calendar format to new format with pickup/delivery separation
  const normalizeCalendar = (cal) => {
    const normalized = {};
    Object.keys(cal || {}).forEach(date => {
      const dayData = cal[date];
      // Check if it's already in new format (has pickup or delivery keys)
      if (dayData.pickup || dayData.delivery) {
        normalized[date] = dayData;
      } else {
        // Legacy format - convert to new format
        normalized[date] = {
          pickup: { ...dayData },
          delivery: { ...dayData }
        };
      }
    });
    return normalized;
  };

  const normalizedCalendar = useMemo(() => normalizeCalendar(calendar), [calendar]);

  useEffect(() => {
    setForm(normalizedCalendar);
  }, [normalizedCalendar])

  useEffect(() => {
    setSyncWindows(syncCalendarWindows !== false);
  }, [syncCalendarWindows])

  const handleSyncToggle = (checked) => {
    setSyncWindows(checked);
    updateShopConfig({ sync_calendar_windows: checked });
  }

  const handleAddDate = () => {
    const baseDay = {
      closed: false,
      windows: [
        { id: 'MO', name: '10am - 2pm', limit: 0, active: true, collection_time: 10, due_time: 14 },
        { id: 'AF', name: '2pm - 5pm', limit: 0, active: true, collection_time: 14, due_time: 17 }
      ]
    };
    const newDay = {
      date: '',
      day: {
        pickup: { ...baseDay },
        delivery: { ...baseDay }
      }
    };
    setEditing(newDay);
  }

  const handleChange = (key, val) => {
    setEditing({
      ...editing,
      [key]: val
    });
  }

  const saveCalendar = (data) => {
    setSaving(true);
    setEditing(false);
    setCalendarPopup(false);
    handleSave(data).finally(() => setSaving(false));
  }

  const handleSaveWithType = (date, dayData, type) => {
    const updatedCalendar = {
      ...form,
      [date]: {
        ...form[date],
        [type]: dayData
      }
    };
    
    // If sync is enabled, apply changes to both pickup and delivery
    if (syncWindows) {
      updatedCalendar[date] = {
        pickup: dayData,
        delivery: dayData
      };
    }
    
    saveCalendar(updatedCalendar);
  }

  const handleRemove = (date) => {
    setSaving(true);
    const updatedCalendar = { ...form };
    delete updatedCalendar[date];
    handleSave(updatedCalendar).finally(() => setSaving(false))
  }

  const saveDisabled = !editing || !editing.date;

  const headers = [
    { type: 'text', label: 'Open', fn: ({ date, day }) =>
      <Checkbox
        disabled={saving}
        checked={day.closed}
        tabIndex={-1}
        checkedIcon={<CloseIcon />}
        icon={<CheckIcon  />}
        onClick={() => handleSaveWithType(date, { ...day, closed: !day.closed }, activeType)}
      />
    },
    { type: 'text', label: 'Date', fn: ({ date }) => date },
    { type: 'text', label: 'Windows', fn: ({ date, day }) => day.closed ? 'Shop is closed.' :
    <div>
      {day.windows.map(window =>
      <span key={window.id}>
        <Checkbox
          disabled={saving}
          checked={window.active}
          onChange={() => handleSaveWithType(date, { ...day, windows: day.windows.map(w => w.id === window.id ? { ...window, active: !window.active } : w) }, activeType)}
          name={window.id}
          color="primary"
        />
        {' ' + window.name}
      </span>
      )}
    </div>
    },
    { type: 'text', label: 'Limit Per Window', fn: ({ date, day }) =>
      <TextField
        type="text"
        min={0}
        value={`${form[date] && form[date][activeType] ? form[date][activeType].windows[0]?.limit || 0 : 0}`}
        onBlur={() => {
          saveCalendar(form)
        }}
        onChange={(val) => {
          if (!isNaN(val)) {
            setForm({ 
              ...form, 
              [date]: { 
                ...form[date],
                [activeType]: { 
                  ...day, 
                  windows: day.windows.map(w => ({ ...w, limit: +val })) 
                }
              }
            })
          }
        }}
      />
    },
    { type: 'numeric', col: null, label: '', fn: ({ date }) => {
      return <Popover
        active={calendarPopup === date}
        activator={saving ? <Spinner accessibilityLabel="Saving" size="small" /> : <MoreIcon style={{cursor: 'pointer'}} onClick={() => setCalendarPopup(date)} />}
        onClose={() => setCalendarPopup(false)}
      >
        <ActionList items={[
          {content: 'Delete', onAction: () => handleRemove(date), destructive: true }
        ]} />
      </Popover>
    }}
  ];

  return (
    <Layout.AnnotatedSection
      title="Calendar"
      description="Override weekly schedule on specific dates for pickup and delivery.">
      <Card>
        <Card.Header
          actions={[
            { content: 'Add', onAction: handleAddDate },
          ]}
          title="Calendar Settings"
        >
        </Card.Header>
        <Card.Section>
          <div style={{ marginBottom: '1rem' }}>
            <ButtonGroup segmented>
              <Button
                pressed={activeType === 'pickup'}
                onClick={() => setActiveType('pickup')}
              >
                Pickup Calendar
              </Button>
              <Button
                pressed={activeType === 'delivery'}
                onClick={() => setActiveType('delivery')}
              >
                Delivery Calendar
              </Button>
            </ButtonGroup>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={syncWindows}
                  onChange={(e) => handleSyncToggle(e.target.checked)}
                  color="primary"
                />
              }
              label="Sync pickup and delivery windows (changes apply to both)"
            />
          </div>
        </Card.Section>
        { Object.keys(form).length > 0 ?
          <DataTable
            columnContentTypes={headers.map(h => h.type)}
            headings={headers.map(h => h.label)}
            rows={Object.keys(form).map(date => {
              const dayData = form[date] && form[date][activeType] ? form[date][activeType] : { closed: false, windows: [] };
              return headers.map(header => header.fn({ date, day: dayData }));
            })}
          />
          :
          <Card.Section>
            <TextContainer>
              Close on certain dates, override order limits and open different pickup and delivery windows.
            </TextContainer>
          </Card.Section>
        }
      </Card>

      <Modal
        title="Add Date"
        open={editing}
        onClose={() => setEditing(false)}
        secondaryActions={[{ content: 'Cancel', onAction: () => setEditing(false) }]}
        primaryAction={{
          content: 'Save', 
          loading: saving, 
          onAction: () => {
            const newCalendar = { ...form, [editing.date]: editing.day };
            saveCalendar(newCalendar);
          }, 
          disabled: saving || saveDisabled 
        }}>
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Date"
              type="date"
              min={0}
              value={`${editing.date}`}
              onChange={(val) => handleChange('date', val)}
            />
            {syncWindows && (
              <TextContainer>
                <p style={{ color: '#637381', fontSize: '0.9rem' }}>
                  Note: Sync is enabled. This date will have the same windows for pickup and delivery.
                </p>
              </TextContainer>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Layout.AnnotatedSection>
  )
}
